/**
 * Category Menu - Slide-out navigation functionality
 * 
 * @package Nautilus
 */

export default function initCategoryMenu() {
    // DOM Elements
    const categoryMenuTrigger = document.getElementById('category-menu-trigger');
    const categoryMenu = document.getElementById('category-menu');
    const categoryMenuOverlay = document.getElementById('category-menu-overlay');
    const closeCategoryMenu = document.getElementById('close-category-menu');
    
    // Get all subcategory panels (they are now outside #category-menu)
    const allSubcategoryPanels = document.querySelectorAll('.subcategory-panel');
    
    // State
    let isMenuOpen = false;

    // Initialize
    function init() {
        if (!categoryMenu || !categoryMenuTrigger) {
            return;
        }

        // Open menu trigger
        categoryMenuTrigger.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            openMenu();
        });

        // Close menu button
        if (closeCategoryMenu) {
            closeCategoryMenu.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                closeMenu();
            });
        }

        // Close on overlay click
        if (categoryMenuOverlay) {
            categoryMenuOverlay.addEventListener('click', (e) => {
                e.preventDefault();
                closeMenu();
            });
        }

        // Close menu when clicking on links inside main menu
        const closeOnClickLinks = categoryMenu.querySelectorAll('.close-menu-on-click');
        closeOnClickLinks.forEach(link => {
            link.addEventListener('click', () => {
                setTimeout(closeMenu, 100);
            });
        });

        // Categories with children - open subcategory panel
        const categoriesWithChildren = categoryMenu.querySelectorAll('.category-with-children');
        categoriesWithChildren.forEach(category => {
            category.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                const categoryId = category.dataset.categoryId;
                openSubcategoryPanel(categoryId);
            });
        });

        // Setup subcategory panels (they are outside #category-menu now)
        allSubcategoryPanels.forEach(panel => {
            // Back to main menu buttons
            const backButton = panel.querySelector('.back-to-main');
            if (backButton) {
                backButton.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const categoryId = backButton.dataset.categoryId;
                    closeSubcategoryPanel(categoryId);
                });
            }

            // Close buttons in subcategory panels
            const closeButton = panel.querySelector('.close-subcategory-menu');
            if (closeButton) {
                closeButton.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    closeMenu();
                });
            }

            // Close menu when clicking on links inside subcategory panels
            const closeOnClickLinksInPanel = panel.querySelectorAll('.close-menu-on-click');
            closeOnClickLinksInPanel.forEach(link => {
                link.addEventListener('click', () => {
                    setTimeout(closeMenu, 100);
                });
            });

            // Expandable children (with grandchildren)
            const expandableChildren = panel.querySelectorAll('.expandable-child');
            expandableChildren.forEach(child => {
                const expandIcon = child.querySelector('.expand-icon');
                if (expandIcon) {
                    expandIcon.addEventListener('click', (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const childId = child.dataset.childId;
                        toggleGrandchildren(childId);
                    });
                }
            });
        });

        // Close menu on Escape key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && isMenuOpen) {
                closeMenu();
            }
        });

        // Close when clicking outside
        document.addEventListener('click', (e) => {
            if (isMenuOpen) {
                // Check if click is outside menu and all subcategory panels
                const isInsideMenu = categoryMenu.contains(e.target);
                const isInsideTrigger = categoryMenuTrigger.contains(e.target);
                const isInsideSubcategoryPanel = Array.from(allSubcategoryPanels).some(panel => panel.contains(e.target));
                
                if (!isInsideMenu && !isInsideTrigger && !isInsideSubcategoryPanel) {
                    closeMenu();
                }
            }
        });
    }

    /**
     * Open the main category menu
     */
    function openMenu() {
        if (!categoryMenu) return;
        
        isMenuOpen = true;
        categoryMenu.classList.remove('-translate-x-full');
        categoryMenu.classList.add('translate-x-0');
        
        if (categoryMenuOverlay) {
            categoryMenuOverlay.classList.remove('hidden');
            // Trigger reflow for animation
            void categoryMenuOverlay.offsetHeight;
            categoryMenuOverlay.classList.remove('opacity-0');
            categoryMenuOverlay.classList.add('opacity-100');
        }
        
        // Prevent body scroll
        document.body.style.overflow = 'hidden';
    }

    /**
     * Close the main category menu
     */
    function closeMenu() {
        if (!categoryMenu) return;
        
        isMenuOpen = false;
        
        // Close all open subcategory panels instantly (they are outside #category-menu now)
        allSubcategoryPanels.forEach(panel => {
            panel.classList.remove('translate-x-0');
            panel.classList.add('-translate-x-full', 'hidden');
            
            // Reset grandchildren
            const grandchildrenLists = panel.querySelectorAll('.grandchildren-list');
            grandchildrenLists.forEach(list => {
                list.classList.add('hidden');
                const icon = list.closest('.expandable-child')?.querySelector('.expand-icon');
                if (icon) {
                    resetExpandIcon(icon);
                }
            });
        });
        
        categoryMenu.classList.remove('translate-x-0');
        categoryMenu.classList.add('-translate-x-full');
        
        if (categoryMenuOverlay) {
            categoryMenuOverlay.classList.remove('opacity-100');
            categoryMenuOverlay.classList.add('opacity-0');
            setTimeout(() => {
                categoryMenuOverlay.classList.add('hidden');
            }, 300);
        }
        
        // Restore body scroll
        document.body.style.overflow = '';
    }

    /**
     * Open a subcategory panel
     * @param {string} categoryId 
     */
    function openSubcategoryPanel(categoryId) {
        const panel = document.getElementById(`subcategory-panel-${categoryId}`);
        if (!panel) return;
        
        // First make it visible, then animate
        panel.classList.remove('hidden');
        // Force reflow
        void panel.offsetHeight;
        panel.classList.remove('-translate-x-full');
        panel.classList.add('translate-x-0');
    }

    /**
     * Close a subcategory panel
     * @param {string} categoryId 
     * @param {boolean} animate - whether to animate the closing
     */
    function closeSubcategoryPanel(categoryId, animate = true) {
        const panel = document.getElementById(`subcategory-panel-${categoryId}`);
        if (!panel) return;

        panel.classList.remove('translate-x-0');
        panel.classList.add('-translate-x-full');
        
        if (animate) {
            setTimeout(() => {
                panel.classList.add('hidden');
            }, 300);
        } else {
            panel.classList.add('hidden');
        }

        // Close any open grandchildren
        const grandchildrenLists = panel.querySelectorAll('.grandchildren-list');
        grandchildrenLists.forEach(list => {
            list.classList.add('hidden');
            const icon = list.closest('.expandable-child')?.querySelector('.expand-icon');
            if (icon) {
                resetExpandIcon(icon);
            }
        });
    }

    /**
     * Toggle grandchildren visibility
     * @param {string} childId 
     */
    function toggleGrandchildren(childId) {
        const grandchildrenList = document.getElementById(`grandchildren-${childId}`);
        const icon = document.querySelector(`.icon-${childId}`);
        
        if (!grandchildrenList) return;

        const isHidden = grandchildrenList.classList.contains('hidden');
        
        if (isHidden) {
            grandchildrenList.classList.remove('hidden');
            if (icon) {
                setMinusIcon(icon);
            }
        } else {
            grandchildrenList.classList.add('hidden');
            if (icon) {
                resetExpandIcon(icon);
            }
        }
    }

    /**
     * Set icon to minus (expanded state)
     * @param {Element} icon 
     */
    function setMinusIcon(icon) {
        icon.setAttribute('data-icon', 'minus');
        icon.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 512 512">
            <path d="M384 265H128v-17h256v17z" fill="currentColor"></path>
        </svg>`;
    }

    /**
     * Reset icon to plus (collapsed state)
     * @param {Element} icon 
     */
    function resetExpandIcon(icon) {
        icon.setAttribute('data-icon', 'plus');
        icon.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 512 512">
            <path d="M384 265H264v119h-17V265H128v-17h119V128h17v120h120v17z" fill="currentColor"></path>
        </svg>`;
    }

    // Initialize on load
    init();

    // Return public API
    return {
        open: openMenu,
        close: closeMenu,
        isOpen: () => isMenuOpen
    };
}
